import {
  CFDEventType,
  Discount,
  LineItem,
  PaymentType,
  SaleCompletedData,
  SaleCompletedEvent,
  SalePaymentReceivedData,
  SalePaymentReceivedEvent,
  SaleUpdatedData,
  TaxItem,
} from '@lightspeed/cfd-event-types'
import { PAYMENT_TYPES } from '@vend/business-components'
import { fetchApi } from '@vend/utilities'
import { GIFTCARDS_ENDPOINT } from '../../components/gift-cards/hooks'
import { getVariantsDisplayNames, hasVariants } from '../../functions/lineItem'
import {
  applyLspCashDiscountToOutstanding,
  getLspCashDiscountAmount,
} from '../../functions/payments'
import { RegisterManager } from '../../managers/register-manager'
import { Payment } from '../../models/payment'
import { SaleStatus } from '../../models/sale.constants'
import {
  convertFulfillmentType,
  convertPaymentTypeIdToEnum,
  getValidProductImageUrl,
} from '../../util/cfd-utils'
import {
  selectDiscountInfo,
  selectLspCashDiscount,
  selectLspCashDiscountPercentage,
  selectSaleDiscount,
  selectSaleReturnFor,
} from '../selectors'
import {
  ReduxCurrentSale,
  selectLineItems,
  selectOutstandingBalance,
  selectSaleFinals,
  selectSaleSummaryFields,
  selectToPay,
} from '../slices/currentSale/currentSaleSlice'
import { lineItemsAdapter } from '../slices/currentSale/lineItemEntityAdapter'
import { selectLspayTippingCalculateBeforeTax } from '../slices/posState/posStateSlice'
import { RootState } from '../store'

const createFormattedLineItems = (
  state: RootState,
  registerManager: RegisterManager
): { lineItems: LineItem[]; hasRefunds: boolean } => {
  const lineItems = selectLineItems(state)
  let hasRefunds = false

  const filteredLineItems = lineItems.filter(lineItem => !lineItem.calculating)

  const formattedLineItems = filteredLineItems.map(lineItem => {
    const basePrice = registerManager.isTaxInclusive()
      ? lineItem.totals.inclusiveBasePrice
      : lineItem.totals.exclusiveBasePrice

    const finalPrice = registerManager.isTaxInclusive()
      ? lineItem.totals.inclusivePrice
      : lineItem.totals.exclusivePrice

    const imageUrl = getValidProductImageUrl(
      lineItem.product.image_thumbnail_url
    )

    const lineItemEntry: LineItem = {
      id: lineItem.localId,
      name: lineItem.product.name,
      quantity: Number(lineItem.quantity),
      unitPrice: Number(lineItem.price),
      basePrice: Number(basePrice),
      finalPrice: Number(finalPrice),
      imageUrl,
    }

    // Determine if this line item is a refund
    const isRefund = lineItem.isReturn || Number(lineItem.quantity) < 0

    if (isRefund) {
      hasRefunds = true
      lineItemEntry.isRefund = true
    }

    // Omit fulfillmentType for refunds - don't add it to lineItemEntry
    if (!isRefund && convertFulfillmentType(lineItem.fulfilmentType)) {
      lineItemEntry.fulfillmentType = convertFulfillmentType(
        lineItem.fulfilmentType
      )
    }

    if (lineItem.promotionDetails && lineItem.promotionDetails.length > 0) {
      lineItemEntry.promotion = {
        name: lineItem.promotionDetails[0].name || '',
        amount: lineItem.promotionDetails[0].amount,
        percentage: Number(lineItem.displayDiscountPercent),
      }
    }
    if (hasVariants(lineItem)) {
      lineItemEntry.variantName = getVariantsDisplayNames(lineItem)
    }

    return lineItemEntry
  })

  return { lineItems: formattedLineItems, hasRefunds }
}

export const createSaleUpdatedPayload = (
  state: RootState,
  registerManager: RegisterManager,
  isCashDiscountActive = false
) => {
  const finals = selectSaleFinals(state)
  const { payAccountBalance } = selectSaleSummaryFields(state)
  const { discountIsPercentage, displayDiscount } = selectDiscountInfo(state)
  const discountInfo = selectSaleDiscount(state)
  const lspCashDiscountPercentage = selectLspCashDiscountPercentage(state)
  const { lineItems, hasRefunds } = createFormattedLineItems(
    state,
    registerManager
  )
  const { payments } = selectToPay(state)

  // Build the Taxes array
  const formattedTaxes: TaxItem[] = Object.values(finals.taxes).map(tax => ({
    name: tax.name,
    amount: tax.total,
  }))

  const saleTotal = Number(finals.inclusivePrice)

  const data: SaleUpdatedData = {
    lineItems,
    currency: registerManager.getActiveOutlet().currency,
    subTotal: Number(finals.subtotal),
    tax: Number(finals.tax),
    taxes: formattedTaxes,
    total: saleTotal,
  }

  // Add discount if it exists
  if (discountInfo) {
    const discount: Discount = {
      amount: Number(discountInfo.price),
    }
    if (discountIsPercentage) {
      discount.percentage = Number(displayDiscount)
    }
    data.discount = discount
  }

  const balanceOutstanding = selectOutstandingBalance(state)

  const returnFor = selectSaleReturnFor(state)

  // Include cashDiscount only when the customer has actively selected
  // cash payment. The discounted total is computed with the same function
  // used by the Cash button (applyLspCashDiscountToOutstanding) so the
  // values always match exactly.
  // Return sales are excluded: the discount is already baked into the
  // original sale totals, so we must not re-apply the adjustment.
  if (
    isCashDiscountActive &&
    !returnFor &&
    lspCashDiscountPercentage > 0 &&
    !(payments.length > 0 && balanceOutstanding > 0)
  ) {
    const discountedTotal = applyLspCashDiscountToOutstanding(
      data.total,
      finals.exclusivePrice,
      lspCashDiscountPercentage,
      finals.tax
    )

    // The inclusive saving is the difference between original and discounted
    // totals. It includes both the flat pre-tax discount and the proportional
    // tax reduction.
    const roundedDiscountedTotal = Number(discountedTotal.toFixed(2))
    const inclusiveSaving = data.total - roundedDiscountedTotal
    const preTaxDiscount = Math.abs(
      Number(
        getLspCashDiscountAmount(
          finals.exclusivePrice,
          lspCashDiscountPercentage
        )
      )
    )
    const taxReduction = inclusiveSaving - preTaxDiscount

    data.cashDiscount = {
      amount: Number(preTaxDiscount.toFixed(2)),
      percentage: lspCashDiscountPercentage,
    }

    data.total = roundedDiscountedTotal
    data.tax = Number((data.tax - taxReduction).toFixed(2))
  }

  // For return sales, the cash discount was already applied on the original
  // sale. Always include it in the CFD payload so the customer sees the
  // discount reflected on the refund, without requiring cfd-cash-payment-selected
  // (which is never dispatched for negative balances).
  // Unlike forward sales, we do NOT adjust data.total or data.tax here because
  // the return totals already reflect the original discounted amounts.
  if (!data.cashDiscount && returnFor && lspCashDiscountPercentage > 0) {
    const lspCashDiscountAmount = -selectLspCashDiscount(state)

    data.cashDiscount = {
      amount: lspCashDiscountAmount,
      percentage: lspCashDiscountPercentage,
    }

    // Temporarily keep lspCashDiscount for returns so the current terminal
    // app (which hasn't been updated to read cashDiscount yet) continues to
    // render the discount on the CFD during refunds. Remove once the updated
    // terminal app is fully rolled out.
    data.lspCashDiscount = {
      amount: lspCashDiscountAmount,
      percentage: lspCashDiscountPercentage,
    }
  }

  // Add account payment if it exists
  if (payAccountBalance) {
    data.accountPayment = Number(payAccountBalance.amount)
    // adjust the total to include account payment - this is not done in finals object
    data.total += Number(payAccountBalance.amount)
  }

  const balancePaid = payments.reduce(
    (total, payment) => total + Number(payment.amount),
    0
  )

  if (balancePaid !== 0) {
    data.balancePaid = balancePaid
    data.balanceOutstanding = balanceOutstanding
  }

  if (hasRefunds) {
    data.hasRefunds = true
  }

  const tipAmount = Number(finals.tip)
  if (tipAmount > 0) {
    data.tip = {
      amount: tipAmount,
      afterTax: !selectLspayTippingCalculateBeforeTax(state),
    }
  }

  const fees = Number(state.currentSale.totals.saleServiceFee)
  if (fees > 0) {
    data.fees = fees
  }

  return {
    timestamp: new Date().toISOString(),
    version: 1,
    type: CFDEventType.SALE_UPDATED,
    data,
  }
}

export const createSaleHiddenPayload = () => ({
  timestamp: new Date().toISOString(),
  version: 1,
  type: CFDEventType.SALE_HIDDEN,
})

export const createSalePaymentReceivedOrCompletedPayload = async (
  sale: ReduxCurrentSale,
  payment: Payment,
  registerManager: RegisterManager,
  type: CFDEventType.SALE_PAYMENT_RECEIVED | CFDEventType.SALE_COMPLETED
): Promise<SalePaymentReceivedEvent | SaleCompletedEvent> => {
  const { lineItems: saleLineItems, discount: discountInfo } = sale
  const lineItems = lineItemsAdapter.getSelectors().selectAll(saleLineItems)

  const data: SalePaymentReceivedData | SaleCompletedData = {
    paymentType: convertPaymentTypeIdToEnum(
      payment.paymentType.type_id
    ) as PaymentType,
    currency: registerManager.getActiveOutlet().currency,
  }

  const { hasRefunds } = createFormattedLineItems(
    { currentSale: sale } as RootState,
    registerManager
  )

  if (hasRefunds) {
    data.hasRefunds = true
  }

  // Check conditions for showSuccess flag
  const hasExactlyOneOriginalPayment = sale.originalSalePayments?.length === 1
  const originalPaymentIsLSPay =
    hasExactlyOneOriginalPayment &&
    sale.originalSalePayments[0].paymentType.type_id === PAYMENT_TYPES.LSPAY
  const currentPaymentIsLSPay =
    payment.paymentType.type_id === PAYMENT_TYPES.LSPAY
  const isFullBalanceRefund =
    sale.finals?.inclusivePrice !== undefined &&
    Number(payment.amount) === Number(sale.finals.inclusivePrice)

  if (
    hasExactlyOneOriginalPayment &&
    originalPaymentIsLSPay &&
    currentPaymentIsLSPay &&
    isFullBalanceRefund
  ) {
    data.showSuccess = true
  }

  // Add discounts if any exist for sale completed payload
  if (type === CFDEventType.SALE_COMPLETED) {
    const totalDiscount = lineItems.reduce((total, lineItem) => {
      const discount = Number(lineItem.displayDiscount) || 0
      return discount > 0 ? total + discount : total
    }, 0)

    const discountAmount = discountInfo
      ? Number(discountInfo.price) + totalDiscount // Combine sale-level and line level discounts
      : totalDiscount // Only line level discounts

    const hasLspCashDiscount = Number(sale.totals.lspCashDiscount) !== 0

    if (discountAmount > 0 || hasLspCashDiscount) {
      data.discount = {
        amount:
          (discountAmount ?? 0) + Number(sale.totals.lspCashDiscount ?? 0) * -1,
      }
    }

    if (hasLspCashDiscount) {
      ;(data as SaleCompletedData).lspCashDiscount = {
        amount: Number(sale.totals.lspCashDiscount),
        percentage: Number(sale.lspCashDiscountPercentage ?? 0),
      }
    }
  }

  if (
    payment.paymentType.type_id === PAYMENT_TYPES.GIFT_CARD &&
    payment.sourceId
  ) {
    try {
      const response = await fetchApi(
        `${GIFTCARDS_ENDPOINT}/transactions/${payment.sourceId}`
      )
      const giftCard = await response.json()
      data.giftCardBalance = Number(giftCard.balance)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching gift card by transactionID', error)
    }
  }

  if (payment.changeAmount) {
    data.change = Number(payment.changeAmount)
  }

  if (sale.change) {
    data.change = Math.abs(Number(sale.change.amount))
  }

  data.lastPaymentAmount = Number(payment.amount)

  return {
    timestamp: new Date().toISOString(),
    version: 1,
    type,
    data,
  }
}

export const createNoPaymentSaleCompletedPayload = async (
  registerManager: RegisterManager,
  saleStatus: SaleStatus
): Promise<SaleCompletedEvent> => {
  // Determine payment type based on sale status
  const paymentType =
    saleStatus === SaleStatus.LAYBY_SALE ? 'LAYBY' : 'CONSUMER_CREDIT_ACCOUNT'

  return {
    timestamp: new Date().toISOString(),
    version: 1,
    type: CFDEventType.SALE_COMPLETED,
    data: {
      paymentType: paymentType as PaymentType,
      currency: registerManager.getActiveOutlet().currency,
      lastPaymentAmount: 0,
    },
  }
}
